import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { WorshipReport } from '../../types/worshipReport';
import { useWindowSize } from '../../hooks/useWindowSize';

interface CategoryChartProps {
  category: 'general' | 'adults' | 'served' | 'children' | 'men' | 'women';
  title: string;
  meetingTypeId?: string;
  startDate: string;
  endDate: string;
}

export function CategoryChart({ category, title, meetingTypeId, startDate, endDate }: CategoryChartProps) {
  const [data, setData] = useState<any[]>([]);
  const { width } = useWindowSize();
  const isMobile = width < 768;

  useEffect(() => {
    const fetchData = async () => {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0); // Set to start of day
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include the entire end date
      
      let baseQuery = query(
        collection(db, 'worshipReports'),
        where('date', '>=', start.toISOString().split('T')[0]),
        where('date', '<=', end.toISOString()),
        orderBy('date', 'asc')
      );

      if (meetingTypeId) {
        baseQuery = query(
          collection(db, 'worshipReports'),
          where('date', '>=', start.toISOString().split('T')[0]),
          where('date', '<=', end.toISOString()),
          where('meetingTypeId', '==', meetingTypeId),
          orderBy('date', 'asc')
        );
      }

      const querySnapshot = await getDocs(baseQuery);
      const reports = querySnapshot.docs.map(doc => ({
        ...doc.data()
      })) as WorshipReport[];
      
      // Transform data based on category
      let chartData = reports.map(report => {
        let boys = 0;
        let girls = 0;
        let men = 0;
        let women = 0;
        let total = 0;

        switch (category) {
          case 'general':
            total = report.totalParticipants;
            break;
          case 'adults':
            men = report.attendance.adults.men;
            women = report.attendance.adults.women;
            total = men + women;
            break;
          case 'served':
            men = report.attendance.served.men;
            women = report.attendance.served.women;
            total = men + women + report.attendance.blooms;
            break;
          case 'children':
            boys = report.attendance.children.boys;
            girls = report.attendance.children.girls;
            total = boys + girls;
            break;
          case 'men':
            men = report.attendance.adults.men + report.attendance.served.men;
            total = men;
            break;
          case 'women':
            women = report.attendance.adults.women + report.attendance.served.women;
            total = women;
            break;
        }

        return {
          date: format(new Date(report.date), isMobile ? 'dd/MM' : 'dd/MM/yy', { locale: fr }),
          boys,
          girls,
          men,
          women,
          total
        };
      });

      // Limit data to last 5 entries on mobile
      if (isMobile && chartData.length > 5) {
        chartData = chartData.slice(-5);
      }

      setData(chartData);
    };

    fetchData();
  }, [category, isMobile, meetingTypeId, startDate, endDate]);

  const renderChart = () => (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart 
        data={data} 
        margin={{ 
          top: 5, 
          right: isMobile ? 10 : 30, 
          left: isMobile ? 0 : 20, 
          bottom: isMobile ? 40 : 25 
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="date" 
          tick={{ fill: '#4B5563', fontSize: isMobile ? 10 : 12 }}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis 
          tick={{ fill: '#4B5563', fontSize: isMobile ? 10 : 12 }}
          width={isMobile ? 30 : 40}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '0.5rem',
            fontSize: isMobile ? 12 : 14
          }}
        />
        <Legend 
          verticalAlign="top" 
          height={36}
          wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
        />
        {category === 'general' ? (
          <Line
            type="monotone"
            dataKey="total"
            stroke="#00665C"
            strokeWidth={2}
            dot={{ fill: '#00665C', r: isMobile ? 3 : 4 }}
            name="Total participants"
          />
        ) : category === 'children' ? (
          <>
            <Bar 
              dataKey="boys" 
              fill="#00665C" 
              name="Garçons"
              barSize={isMobile ? 15 : 20}
            />
            <Bar 
              dataKey="girls" 
              fill="#F2B636" 
              name="Filles"
              barSize={isMobile ? 15 : 20}
            />
          </>
        ) : category !== 'men' && category !== 'women' && (
          <>
            <Bar 
              dataKey="men" 
              fill="#00665C" 
              name="Hommes"
              barSize={isMobile ? 15 : 20}
            />
            <Bar 
              dataKey="women" 
              fill="#F2B636" 
              name="Femmes"
              barSize={isMobile ? 15 : 20}
            />
          </>
        )}
        {category !== 'general' && (
          <Line
            type="monotone"
            dataKey="total"
            stroke="#b12029"
            strokeWidth={2}
            dot={{ fill: '#b12029', r: isMobile ? 3 : 4 }}
            name="Total"
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  return (
    <div className="w-full h-full">
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="h-[calc(100%-2rem)]">
        {renderChart()}
      </div>
    </div>
  );
}